'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { useIsClient } from '../../lib/hooks/useIsClient';
import { c } from '../../utils/tailwind-merge';
import { Alert } from '../Alert/Alert';
import { EntitlementConfirmation } from '../EntitlementConfirmation/EntitlementConfirmation';
import { Gallery } from '../Gallery/Gallery';
import { ProductBreadcrumbs } from '../ProductBreadcrumbs/ProductBreadcrumbs';
import { ProductInfo } from '../ProductInfo/ProductInfo';
import { ProductNutritionLabels } from '../ProductNutritionLabels/ProductNutritionLabels';
import { ProductTabs } from '../ProductTabs/ProductTabs';
import { ProductTabValue } from '../ProductTabs/types';
import { RelatedKeywords } from '../RelatedKeywords/RelatedKeywords';
import { Separator } from '../Separator/Separator';
import { UpmProductDetailTemplateProps } from './types';

export const UpmProductDetailTemplate = ({
  breadcrumbs,
  currentTab,
  entitlementConfirmation,
  ownedDate,
  gallery,
  productInfo,
  nutritionLabels,
  tabs,
  relatedKeywords,
  wasEntitled,
  microcopy,
  className = '',
  onClickChipList,
  onClickProductInfo,
  onOpenInUnityFromEntitlement,
  onGoToMyAssets,
  onSeeOwnedPackages,
  onOpenInUnityFromPackage,
  onCloseFromEntitlement,
  onHandleTabChange,
  onOpenCookieSettings,
}: UpmProductDetailTemplateProps) => {
  const isClient = useIsClient();
  const [activeTab, setActiveTab] = useState<ProductTabValue>(currentTab);

  const [isProductInfoSticky, setIsProductInfoSticky] = useState(false);
  const prevStickyRef = useRef(false);

  const desktopObserverRef = useRef<IntersectionObserver | null>(null);
  const mobileObserverRef = useRef<IntersectionObserver | null>(null);

  const makeObserver = useCallback(
    (el: HTMLElement, threshold: number) => {
      if (!isClient) return null;

      const cb: IntersectionObserverCallback = (entries) => {
        const entry = entries[0];

        if (!el || el.offsetParent === null) return;

        const nextSticky = !entry.isIntersecting;

        if (nextSticky !== prevStickyRef.current) {
          prevStickyRef.current = nextSticky;
          setIsProductInfoSticky(nextSticky);
        }
      };

      const obs = new IntersectionObserver(cb, {
        root: null,
        threshold,
      });

      obs.observe(el);
      return obs;
    },
    [isClient]
  );

  const onDesktopCtaMount = useCallback(
    (el: HTMLElement | null) => {
      if (desktopObserverRef.current) {
        desktopObserverRef.current.disconnect();
        desktopObserverRef.current = null;
      }
      if (el && isClient) {
        const observer = makeObserver(el, 0);
        if (observer) {
          desktopObserverRef.current = observer;
        }
      }
    },
    [makeObserver, isClient]
  );

  const onMobileCtaMount = useCallback(
    (el: HTMLElement | null) => {
      if (mobileObserverRef.current) {
        mobileObserverRef.current.disconnect();
        mobileObserverRef.current = null;
      }
      if (el && isClient) {
        const observer = makeObserver(el, 0.2);
        if (observer) {
          mobileObserverRef.current = observer;
        }
      }
    },
    [makeObserver, isClient]
  );

  useEffect(() => {
    return () => {
      desktopObserverRef.current?.disconnect();
      mobileObserverRef.current?.disconnect();
    };
  }, []);

  const handleTabChange = useCallback(
    (tab: ProductTabValue) => {
      setActiveTab(tab);
      onHandleTabChange(tab);
    },
    [onHandleTabChange]
  );

  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  const renderPurchasedItemAlert = () => {
    return ownedDate ? (
      <Alert
        label={interpolateStringWithData(microcopy.ownedDate, {
          date: ownedDate,
        })}
        className="my-4"
      />
    ) : null;
  };

  const renderDesktop = () => (
    <section className="hidden lg:block">
      {wasEntitled && (
        <EntitlementConfirmation
          {...entitlementConfirmation}
          microcopy={microcopy.entitlementConfirmation}
          onGoToMyAssets={onGoToMyAssets}
          onOpenInUnityFromEntitlement={onOpenInUnityFromEntitlement}
          onSeeOwnedPackages={onSeeOwnedPackages}
          onCloseFromEntitlement={onCloseFromEntitlement}
        />
      )}

      {isProductInfoSticky && (
        <ProductInfo
          {...productInfo}
          variant="sticky"
          microcopy={microcopy.productInfo}
          onClickProductInfo={onClickProductInfo}
        />
      )}

      <div className="lg-container w-full">
        {renderPurchasedItemAlert()}
        <ProductBreadcrumbs {...breadcrumbs} className="mb-1" />

        <div className="grid grid-cols-[minmax(0,_1fr)_396px] items-start gap-6">
          <div className="flex min-w-0 flex-col gap-6 self-start">
            <Gallery
              {...gallery}
              microcopy={microcopy.gallery}
              position="details"
              aspectRatio="aspect-[3/2]"
              onOpenCookieSettings={onOpenCookieSettings}
            />
            <ProductTabs
              {...tabs}
              microcopy={microcopy.tabs}
              value={activeTab}
              onValueChange={handleTabChange}
              onOpenInUnityFromPackage={onOpenInUnityFromPackage}
            />
            <Separator className="mt-16" />
            <RelatedKeywords
              {...relatedKeywords}
              microcopy={microcopy.relatedKeywords}
              onClickChipList={onClickChipList}
            />
          </div>

          <aside className="flex flex-col gap-6 lg:shrink-0">
            <div
              aria-hidden={isProductInfoSticky}
              className={
                isProductInfoSticky
                  ? 'pointer-events-none select-none px-4 opacity-0'
                  : ''
              }
            >
              <ProductInfo
                {...productInfo}
                variant="default"
                microcopy={microcopy.productInfo}
                onClickProductInfo={onClickProductInfo}
                onCallToActionMount={onDesktopCtaMount}
              />
            </div>

            <ProductNutritionLabels
              {...nutritionLabels}
              microcopy={microcopy.nutritionLabels}
            />
          </aside>
        </div>
      </div>
    </section>
  );

  const renderMobile = () => (
    <section
      className={c(
        'grid grid-cols-1 gap-6 lg:hidden',
        isProductInfoSticky && 'pb-20'
      )}
    >
      {isProductInfoSticky && (
        <ProductInfo
          {...productInfo}
          variant="sticky"
          microcopy={microcopy.productInfo}
          onClickProductInfo={onClickProductInfo}
        />
      )}

      {renderPurchasedItemAlert()}
      <ProductBreadcrumbs {...breadcrumbs} className="mb-0 px-4" />

      <div
        aria-hidden={isProductInfoSticky}
        className={
          isProductInfoSticky
            ? 'pointer-events-none select-none opacity-5'
            : 'p-5'
        }
      >
        <ProductInfo
          {...productInfo}
          variant="default"
          microcopy={microcopy.productInfo}
          onClickProductInfo={onClickProductInfo}
          onCallToActionMount={onMobileCtaMount}
        />
      </div>

      <Gallery
        {...gallery}
        position="details"
        microcopy={microcopy.gallery}
        aspectRatio="aspect-[3/2]"
        onOpenCookieSettings={onOpenCookieSettings}
      />
      <ProductTabs
        {...tabs}
        microcopy={microcopy.tabs}
        value={activeTab}
        onValueChange={onHandleTabChange}
        onOpenInUnityFromPackage={onOpenInUnityFromPackage}
        className="px-4"
      />
      <div>
        <ProductNutritionLabels
          {...nutritionLabels}
          microcopy={microcopy.nutritionLabels}
          className="px-4"
        />
        <Separator />
        <RelatedKeywords
          {...relatedKeywords}
          microcopy={microcopy.relatedKeywords}
          className="px-4"
          onClickChipList={onClickChipList}
        />
      </div>
    </section>
  );

  return (
    <main className={className}>
      {renderDesktop()}
      {renderMobile()}
    </main>
  );
};
